import { useState, useMemo, useRef, useEffect } from 'react';
import { Shield, RefreshCw, Bell, Sun, Moon, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import ExportButton from './ExportButton';

function Header({ theme, onThemeToggle, onRefresh, lastUpdated, onGenerateReport, shipments, excursions, onSelectShipment, onNavigateToAlerts }) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { colors } = useTheme();
  const dropdownRef = useRef(null);

  const alerts = useMemo(() => {
    if (!shipments || !excursions) return [];
    const list = [];
    const now = new Date();
    excursions.filter(e => e.severity === 'Critical').slice(0, 3).forEach((exc, i) => {
      const shipment = shipments.find(s => s.id === exc.shipmentId);
      list.push({
        id: `c-${exc.id}`,
        type: 'critical',
        title: 'Critical Excursion',
        message: `${shipment?.productName?.split(' ')[0] || 'Shipment'} +${exc.maxDeviationC}°C`,
        timestamp: new Date(now - (i * 15) * 60000),
        shipmentId: exc.shipmentId,
      });
    });
    return list;
  }, [shipments, excursions]);

  const criticalCount = alerts.length;

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    onRefresh();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const formatTime = (date) => new Date(date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  return (
    <header style={{
      background: colors.bgCard,
      backgroundImage: colors.gradientCard,
      padding: '1rem 2rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
      position: 'relative',
      zIndex: 100,
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: '10%',
        right: '10%',
        height: '100%',
        background: colors.gradientGlow,
        pointerEvents: 'none',
      }} />

      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', position: 'relative' }}>
        <div style={{
          width: '42px',
          height: '42px',
          borderRadius: '12px',
          background: colors.gradientPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: colors.primaryGlow,
        }}>
          <Shield size={22} color="white" />
        </div>
        <div>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em' }}>
            ChainGuard
          </div>
          <div style={{ fontSize: '0.65rem', color: colors.textMuted, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Cold Chain Compliance
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', position: 'relative' }}>
        <span style={{ fontSize: '0.7rem', color: colors.textMuted, marginRight: '0.5rem' }}>
          Updated {formatTime(lastUpdated)}
        </span>
        
        <ExportButton shipments={shipments} excursions={excursions} />
        
        {/* Report Button - Premium */}
        <button
          onClick={onGenerateReport}
          style={{
            padding: '0.55rem 1rem',
            background: colors.gradientPrimary,
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            boxShadow: colors.primaryGlow,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 0 40px rgba(0, 212, 170, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = colors.primaryGlow;
          }}
        >
          <FileText size={15} />
          Report
        </button>
        
        {/* Icon Buttons */}
        {[
          { icon: RefreshCw, onClick: handleRefresh, spin: isRefreshing },
          { icon: Bell, onClick: () => setShowNotifications(!showNotifications), badge: criticalCount, ref: dropdownRef },
          { icon: theme === 'dark' ? Sun : Moon, onClick: onThemeToggle, special: true },
        ].map((btn, i) => (
          <div key={i} style={{ position: 'relative' }} ref={btn.ref}>
            <button
              onClick={btn.onClick}
              style={{
                padding: '0.55rem',
                background: btn.special 
                  ? (theme === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(99, 102, 241, 0.1)')
                  : colors.bgElevated,
                border: 'none',
                borderRadius: '10px',
                color: btn.special 
                  ? (theme === 'dark' ? '#ffc107' : '#6366f1')
                  : colors.textSecondary,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
                animation: btn.spin ? 'spin 1s linear infinite' : 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.bgElevated;
                e.currentTarget.style.color = colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = btn.special 
                  ? (theme === 'dark' ? 'rgba(255, 193, 7, 0.1)' : 'rgba(99, 102, 241, 0.1)')
                  : colors.bgElevated;
                e.currentTarget.style.color = btn.special 
                  ? (theme === 'dark' ? '#ffc107' : '#6366f1')
                  : colors.textSecondary;
              }}
            >
              <btn.icon size={17} />
            </button>
            {btn.badge > 0 && (
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                width: '18px',
                height: '18px',
                background: colors.danger,
                borderRadius: '50%',
                fontSize: '0.6rem',
                color: 'white',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: colors.dangerGlow,
                animation: 'pulse 2s infinite',
              }}>
                {btn.badge}
              </span>
            )}
          </div>
        ))}

        {/* Notifications Dropdown */}
        {showNotifications && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 12px)',
            right: '60px',
            width: '320px',
            background: colors.bgCard,
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease',
          }}>
            <div style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.bgSecondary }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary }}>Notifications</span>
              <button onClick={() => setShowNotifications(false)} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '0.25rem' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
              {alerts.length > 0 ? alerts.map(alert => (
                <div
                  key={alert.id}
                  onClick={() => { onSelectShipment(shipments.find(s => s.id === alert.shipmentId)); setShowNotifications(false); }}
                  style={{
                    padding: '0.85rem 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    gap: '0.75rem',
                    alignItems: 'flex-start',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = colors.tableRowHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: colors.dangerMuted, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AlertCircle size={16} color={colors.danger} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: colors.textPrimary }}>{alert.title}</div>
                    <div style={{ fontSize: '0.7rem', color: colors.textSecondary }}>{alert.message}</div>
                  </div>
                </div>
              )) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
                  <CheckCircle size={28} color={colors.success} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                  <div style={{ fontSize: '0.8rem' }}>All clear</div>
                </div>
              )}
            </div>
            {alerts.length > 0 && (
              <div style={{ padding: '0.75rem', textAlign: 'center', background: colors.bgSecondary }}>
                <button onClick={() => { setShowNotifications(false); onNavigateToAlerts(); }} style={{ background: 'none', border: 'none', color: colors.primary, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                  View All Alerts
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
      `}</style>
    </header>
  );
}

export default Header;